import {
  GAME_NAME,
  MAX_BASE,
  MAX_TOTAL,
  MIN_YEAR,
  SCORING,
  TIER_COLOR,
  TIER_EMOJI,
  TIER_LABEL,
  whenPoints,
  wherePoints,
} from '@geosport/shared';
import { Shell } from '../components/Shell';
import { Link } from '../lib/router';
import { useSettings } from '../lib/settings';

const MAX_KM = 6000;

/** Where-points curve, drawn with plain SVG (no chart library, PRD §10). */
function WhereCurve() {
  const units = useSettings((s) => s.units);
  const w = 320;
  const h = 150;
  const padL = 30;
  const padB = 24;
  const x = (km: number): number => padL + (km / MAX_KM) * (w - padL - 14);
  const y = (pts: number): number => 8 + (1 - pts / SCORING.wherePoints) * (h - padB - 8);
  const points = Array.from({ length: 61 }, (_, i) => (i * MAX_KM) / 60).map(
    (km) => `${x(km).toFixed(1)},${y(wherePoints(km)).toFixed(1)}`,
  );
  // Round numbers in the unit being shown; positions stay in km.
  const marks =
    units === 'mi'
      ? [250, 500, 1000, 2000, 3500].map((mi) => ({ km: mi * 1.609344, text: mi }))
      : [500, 1000, 2000, 4000, 5500].map((km) => ({ km, text: km }));
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="w-full"
      role="img"
      aria-label="Where points fall with distance"
    >
      {[0, 35, 70].map((p) => (
        <g key={p}>
          <line x1={padL} x2={w - 14} y1={y(p)} y2={y(p)} stroke="rgba(255,255,255,0.1)" />
          <text
            x={padL - 6}
            y={y(p) + 4}
            textAnchor="end"
            fontSize="10"
            fill="rgba(255,255,255,0.5)"
          >
            {p}
          </text>
        </g>
      ))}
      <polyline points={points.join(' ')} fill="none" stroke="#f5b301" strokeWidth="2.5" />
      {marks.map(({ km, text }) => (
        <g key={km}>
          <circle cx={x(km)} cy={y(wherePoints(km))} r="3.5" fill="white" />
          <text x={x(km)} y={y(wherePoints(km)) - 8} textAnchor="middle" fontSize="10" fill="white">
            {wherePoints(km)}
          </text>
          <text x={x(km)} y={h - 6} textAnchor="middle" fontSize="10" fill="rgba(255,255,255,0.6)">
            {text.toLocaleString('en-GB')}
          </text>
        </g>
      ))}
      <text x={w - 2} y={h - 6} textAnchor="end" fontSize="9" fill="rgba(255,255,255,0.4)">
        {units}
      </text>
    </svg>
  );
}

function Multipliers() {
  const max = MAX_BASE * 3;
  return (
    <div className="flex items-end gap-2" role="img" aria-label="Points available per question">
      {SCORING.multipliers.map((m, i) => (
        <div key={i} className="flex flex-1 flex-col items-center gap-1 text-xs">
          <span className="font-semibold tabular-nums">{MAX_BASE * m}</span>
          <div
            className="w-full rounded-t-md bg-accent/80"
            style={{ height: `${((MAX_BASE * m) / max) * 80}px` }}
          />
          <span className="text-white/60">
            Q{i + 1} {m > 1 && `×${m}`}
          </span>
        </div>
      ))}
    </div>
  );
}

export function HowItWorks() {
  const bands: [keyof typeof TIER_EMOJI, string][] = [
    ['bullseye', '100 — perfect, and it glows on the results page'],
    ['green', '80–99'],
    ['yellow', '50–79'],
    ['orange', '20–49'],
    ['red', '0–19'],
  ];
  return (
    <Shell title={`How ${GAME_NAME} works`}>
      <div className="flex flex-col gap-7 text-sm leading-relaxed text-white/80">
        <section>
          <h2 className="mb-2 text-base font-semibold text-white">The basics</h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>Five sports moments every day, the same five for everyone.</li>
            <li>For each one, tap the globe where it happened, then slide to the year.</li>
            <li>The closer your pin and your year, the more points you score.</li>
            <li>New questions drop at midnight UK time. Your progress survives a refresh.</li>
            <li>Play every day to build a streak; miss a day and it resets.</li>
          </ul>
        </section>

        <section>
          <h2 className="mb-2 text-base font-semibold text-white">How each question is scored</h2>
          <p>
            Every question is worth up to {MAX_BASE} points: {SCORING.wherePoints} for{' '}
            <strong className="text-white">where</strong> and {SCORING.whenPoints} for{' '}
            <strong className="text-white">when</strong>.
          </p>
          <div className="mt-3 rounded-2xl bg-white/5 p-3">
            <div className="mb-1 text-xs font-semibold text-white/60 uppercase">
              Where points by distance
            </div>
            <WhereCurve />
          </div>
          <p className="mt-2">
            Within {SCORING.fullMarksRadiusKm} km is full marks. The curve is steep at first and
            softens with distance, so a continental miss still scores something.
          </p>
          <p className="mt-2">
            <strong className="text-white">When:</strong> the exact year scores {SCORING.whenPoints}
            . Each year off costs {SCORING.pointsPerYear} points (
            {[1, 3, 5, 10].map((d) => `${d} off → ${whenPoints(d)}`).join(', ')}). The slider runs
            from {MIN_YEAR} to the current year.
          </p>
        </section>

        <section>
          <h2 className="mb-2 text-base font-semibold text-white">
            Later questions are worth more
          </h2>
          <div className="rounded-2xl bg-white/5 p-3">
            <Multipliers />
          </div>
          <p className="mt-2">
            A perfect day is {SCORING.multipliers.map((m) => MAX_BASE * m).join(' + ')} ={' '}
            {MAX_TOTAL.toLocaleString('en-GB')} points.
          </p>
        </section>

        <section>
          <h2 className="mb-2 text-base font-semibold text-white">Colours and emoji</h2>
          <p>The dot you see on each question, the results grid and shared posts shows the tier:</p>
          <ul className="mt-2 grid gap-1">
            {bands.map(([tier, band]) => (
              <li key={tier} className="flex items-center gap-2">
                <span>{TIER_EMOJI[tier]}</span>
                <span className="w-20 font-semibold" style={{ color: TIER_COLOR[tier] }}>
                  {TIER_LABEL[tier]}
                </span>
                <span className="text-white/70">{band}</span>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="mb-2 text-base font-semibold text-white">Leaderboard</h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              The top 50 scores of the day, ties broken by time from first clue to last guess.
            </li>
            <li>Your own row is always shown, even outside the top 50.</li>
            <li>Browse the previous 14 days with the arrows.</li>
            <li>
              Pick a display name in Settings; if it’s taken that day, a number is added (Sam,
              Sam2).
            </li>
            <li>
              <Link to="/groups" className="text-accent underline underline-offset-2">
                Groups
              </Link>{' '}
              are private leaderboards for friends: create one and share the invite link.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="mb-2 text-base font-semibold text-white">Streaks, stats and practice</h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>Your streak counts consecutive days with a finished round.</li>
            <li>
              <Link to="/stats" className="text-accent underline underline-offset-2">
                Stats
              </Link>{' '}
              shows a 7-day chart, your distribution and history.
            </li>
            <li>
              <Link to="/practice" className="text-accent underline underline-offset-2">
                Practice
              </Link>{' '}
              rounds (random questions, or replaying previous days) are scored but never ranked and
              never affect your streak.
            </li>
            <li>
              No account needed: to keep your streak on another device, use “Get a code” in
              Settings.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="mb-2 text-base font-semibold text-white">Tips</h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>Drag to spin the globe, pinch or use the + / − buttons to zoom.</li>
            <li>After a tap, drag the pin to fine-tune before locking it in.</li>
            <li>
              Read the clue for the sport and era: the year slider is worth 30 points a question.
            </li>
            <li>Switch between satellite and dark maps, or km and miles, in Settings.</li>
          </ul>
        </section>

        <section className="text-xs text-white/50">
          Map data © OpenStreetMap contributors via Protomaps; satellite imagery © Esri, Maxar,
          Earthstar Geographics and the GIS User Community; region outlines from Natural Earth.
        </section>

        <p className="text-xs text-white/40">
          <Link to="/">← Back home</Link> · <a href="/faq">FAQ</a> ·{' '}
          <a href="/puzzles">Past puzzles with answers</a> · <Link to="/privacy">Privacy</Link> ·{' '}
          <Link to="/terms">Terms</Link>
        </p>
      </div>
    </Shell>
  );
}
